import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleInput } from 'app/shared/input';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { ReactComponent as Icon } from 'assets/icons/logo-sm.svg';
import { updateDataToStore } from 'store/actions/user-data';
import { useOrganizationService } from 'hooks/organization';
import {
  SCREENS,
  ROLE_OPTIONS,
  GOAL_OPTIONS,
  CHANNEL_OPTIONS,
  LIST_OPTIONS,
  TRANSACTIONAL_OPTIONS,
  DNS_OPTIONS,
  TIMELINE_OPTIONS,
  buildProgressPayload
} from './data';
import { OptionCard } from './OptionCard';
import { QuestionScreen } from './QuestionScreen';
import styles from '../organization.module.css';

export const QualificationFlow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tenant_id } = useSelector((state) => state.user_data);
  const { saveQualificationProgress, submitQualification } = useOrganizationService();

  const [screen, setScreen] = useState(SCREENS.WELCOME);
  const [answers, setAnswers] = useState({
    role: null,
    goal: null,
    channels: [],
    list_situation: null,
    transactional_setup: null,
    dns_confidence: null,
    timeline: null,
    referral_code: ''
  });
  const [saving, setSaving] = useState(false);
  const screenEnteredAt = useRef(Date.now());

  const isTransactional = answers.goal === 'transactional';
  const showChannels =
    (answers.role === 'marketer' || answers.role === 'founder') && !isTransactional;

  const totalSteps = showChannels ? 6 : 5;
  const progressStep = screen <= SCREENS.GOAL ? screen : showChannels ? screen : screen - 1;
  const progress = screen > 0 ? (Math.min(progressStep, totalSteps) / totalSteps) * 100 : 0;

  const setAnswer = (field, value) => setAnswers((prev) => ({ ...prev, [field]: value }));

  const goNext = () => {
    if (screen === SCREENS.GOAL && !showChannels) {
      setScreen(SCREENS.LIST);
    } else {
      setScreen((s) => s + 1);
    }
  };

  const goBack = () => {
    if (screen === SCREENS.LIST && !showChannels) {
      setScreen(SCREENS.GOAL);
    } else {
      setScreen((s) => s - 1);
    }
  };

  // Reset the per-screen timer whenever the screen changes
  useEffect(() => {
    screenEnteredAt.current = Date.now();
  }, [screen]);

  // Record flow entry on welcome screen mount
  useEffect(() => {
    saveQualificationProgress(tenant_id, {
      data: {
        'onboarding.started_at': new Date().toISOString(),
        'onboarding.last_screen_seen': 'welcome'
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = (currentScreen) => {
    const t = Math.round((Date.now() - screenEnteredAt.current) / 1000);
    const data = buildProgressPayload(currentScreen, answers, t, isTransactional, showChannels);
    saveQualificationProgress(tenant_id, { data });
    goNext();
  };

  const toggleChannel = (value) => {
    setAnswers((prev) => {
      const next = prev.channels.includes(value)
        ? prev.channels.filter((c) => c !== value)
        : [...prev.channels, value];
      return { ...prev, channels: next };
    });
  };

  const handleSubmit = async (referral_code = '') => {
    setSaving(true);
    const result = await submitQualification(tenant_id, {
      data: { referral_code: referral_code || null, complete: true }
    });
    setSaving(false);

    if (!result) return;

    dispatch(updateDataToStore({ onboarding_completed: true }));
    navigate('/dashboard');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Icon />
        <span className={styles.siteName} aria-label="Go-Mailer">
          Go-Mailer<span className={styles.stub}>.</span>
        </span>
      </div>

      <div className={styles.formWrapper}>
        {screen > 0 && (
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>
        )}

        {screen === SCREENS.WELCOME && (
          <div className={styles.welcomeContent}>
            <div className={styles.welcomeHeadline}>You're in.</div>
            <p className={styles.welcomeBody}>
              We'd love to know a little about you first. Five questions — then everything is set up
              around you, not everyone else.
            </p>
            <div className={styles.pills}>
              <span className={styles.pill}>90 seconds</span>
              <span className={styles.pill}>Makes it yours</span>
              <span className={styles.pill}>Private</span>
            </div>
            <Button className={styles.btnPrimary} text="I'm ready" onClick={goNext} />
          </div>
        )}

        {screen === SCREENS.ROLE && (
          <QuestionScreen
            title="What best describes you?"
            subtext="This helps us set up Go-Mailer the right way for you from day one."
            options={ROLE_OPTIONS}
            value={answers.role}
            onSelect={(val) => setAnswer('role', val)}
            onBack={goBack}
            onContinue={() => handleContinue(SCREENS.ROLE)}
          />
        )}

        {screen === SCREENS.GOAL && (
          <QuestionScreen
            title="What's the main reason you're here?"
            subtext="Pick the one that matters most right now."
            options={GOAL_OPTIONS}
            value={answers.goal}
            onSelect={(val) => setAnswer('goal', val)}
            onBack={goBack}
            onContinue={() => handleContinue(SCREENS.GOAL)}
          />
        )}

        {screen === SCREENS.CHANNELS && (
          <>
            <div className={styles.questionTitle}>Which channels do you plan to use?</div>
            <p className={styles.questionSubtext}>
              Select everything that applies — we'll set up the right tools for you.
            </p>
            <div className={styles.optionsGrid}>
              {CHANNEL_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.value}
                  label={opt.label}
                  description={opt.description}
                  selected={answers.channels.includes(opt.value)}
                  onClick={() => toggleChannel(opt.value)}
                />
              ))}
            </div>
            {answers.channels.length === 0 && (
              <p className={styles.validationHint}>Pick at least one — this shapes your setup.</p>
            )}
            <div className={styles.actionRow}>
              <GridRow num_of_columns={2}>
                <GridColumn>
                  <Button
                    type="secondary"
                    className={styles.btnSecondary}
                    text="← Back"
                    onClick={goBack}
                  />
                </GridColumn>
                <GridColumn>
                  <Button
                    className={styles.btnPrimary}
                    text="Continue →"
                    onClick={() => handleContinue(SCREENS.CHANNELS)}
                    disabled={answers.channels.length === 0}
                  />
                </GridColumn>
              </GridRow>
            </div>
          </>
        )}

        {screen === SCREENS.LIST && (
          <QuestionScreen
            title={
              isTransactional
                ? 'Who will be handling the API integration?'
                : 'Where is your contact list right now?'
            }
            subtext={
              isTransactional
                ? 'Go-Mailer sends transactional emails via API. This helps us point the right person to the docs.'
                : "We'll help you get it into Go-Mailer the fastest way possible."
            }
            options={isTransactional ? TRANSACTIONAL_OPTIONS : LIST_OPTIONS}
            value={isTransactional ? answers.transactional_setup : answers.list_situation}
            onSelect={(val) =>
              setAnswer(isTransactional ? 'transactional_setup' : 'list_situation', val)
            }
            onBack={goBack}
            onContinue={() => handleContinue(SCREENS.LIST)}
          />
        )}

        {screen === SCREENS.DNS && (
          <QuestionScreen
            title="When it comes to setting up email authentication (DNS records), how comfortable are you?"
            subtext="This affects your inbox delivery rate. We handle the complexity — we just need to know how much hand-holding to give you."
            options={DNS_OPTIONS}
            value={answers.dns_confidence}
            onSelect={(val) => setAnswer('dns_confidence', val)}
            onBack={goBack}
            onContinue={() => handleContinue(SCREENS.DNS)}
          />
        )}

        {screen === SCREENS.TIMELINE && (
          <QuestionScreen
            title="How soon are you looking to send your first message?"
            subtext="No pressure — there's no wrong answer. We'll pace things accordingly."
            options={TIMELINE_OPTIONS}
            value={answers.timeline}
            onSelect={(val) => setAnswer('timeline', val)}
            onBack={goBack}
            onContinue={() => handleContinue(SCREENS.TIMELINE)}
            continueLabel="Complete →"
          />
        )}

        {screen === SCREENS.REFERRAL && (
          <>
            <div className={styles.questionTitle}>Were you referred by someone?</div>
            <p className={styles.questionSubtext}>
              Enter their referral code if you have one — it helps us credit them.
            </p>
            <GridRow num_of_columns={1}>
              <GridColumn>
                <DetailItem dark title="Referral Code">
                  <SimpleInput
                    dark
                    input_id="referral-code"
                    value={answers.referral_code}
                    onInput={(value) => setAnswer('referral_code', value)}
                  />
                </DetailItem>
              </GridColumn>
            </GridRow>
            <div className={styles.actionRow}>
              <GridRow num_of_columns={2}>
                <GridColumn>
                  <Button
                    type="secondary"
                    className={styles.btnSecondary}
                    text="← Back"
                    onClick={goBack}
                    disabled={saving}
                  />
                </GridColumn>
                <GridColumn>
                  <Button
                    className={styles.btnPrimary}
                    text="Take me to my dashboard →"
                    onClick={() => handleSubmit(answers.referral_code)}
                    disabled={saving}
                    loading={saving}
                  />
                </GridColumn>
              </GridRow>
              <Button
                className={styles.skipLink}
                text="No referral code — take me to my dashboard →"
                onClick={() => handleSubmit('')}
                disabled={saving}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
